<template>
  <ul class="workspace-list">
    <li v-for="workspace in workspaces" :key="workspace.id" class="workspace-row">
      <span class="workspace-name" :title="workspace.name">{{ workspace.name }}</span>

      <div v-if="hasAccess(workspace)" class="capability-chips">
        <button
          v-for="capability in allCapabilities"
          :key="capability"
          type="button"
          class="capability-chip"
          :class="[capabilityState(workspace, capability), { locked: isLocked(workspace, capability) }]"
          :disabled="isLocked(workspace, capability) || !isOfferable(workspace, capability)"
          :aria-pressed="String(isGranted(workspace, capability))"
          :aria-label="`${capabilityLabels[capability]} in ${workspace.name}`"
          :title="
            isLocked(workspace, capability) ? `An agent with access to ${workspace.name} can always read it` : null
          "
          @click="toggleCapability(workspace, capability)"
        >
          {{ capabilityLabels[capability] }}
        </button>
        <button
          type="button"
          class="workspace-drop"
          :aria-label="`Remove this agent's access to ${workspace.name}`"
          @click="dropWorkspace(workspace)"
        >
          <!-- Inline: the shared tick asset carries no viewBox, so it cannot be drawn at this size. -->
          <svg aria-hidden="true" width="12" height="9" viewBox="0 0 12 9" fill="none">
            <path
              d="M1 4.5 4.2 7.7 11 1"
              stroke="currentColor"
              stroke-width="1.6"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </div>

      <div v-else class="workspace-none">
        <span class="no-access">No access</span>
        <button
          type="button"
          class="workspace-add"
          :aria-label="`Give this agent access to ${workspace.name}`"
          @click="addWorkspace(workspace)"
        >
          <span class="workspace-add-plus" aria-hidden="true">+</span> Add
        </button>
      </div>
    </li>
  </ul>
</template>

<script>
/**
 * One row per workspace the human may delegate in, each carrying its own capabilities — what the
 * form posts as one `grids_capability` entry per workspace (public-api authorizationDetails.ts).
 *
 * `value` maps workspace id -> capabilities; a workspace missing from it gets no access. It is
 * emitted whole because Vue 2 tracks neither added nor deleted keys.
 */
export const ALL_CAPABILITIES = ['read', 'write', 'share', 'publish'];

/** `write` reads as "Edit" to a human deciding what an agent may do to their work. */
export const CAPABILITY_LABELS = { read: 'Read', write: 'Edit', share: 'Share', publish: 'Publish' };

export default {
  name: 'WorkspacePermissions',
  props: {
    workspaces: { type: Array, required: true },
    value: { type: Object, required: true }
  },
  data() {
    return {
      allCapabilities: ALL_CAPABILITIES,
      capabilityLabels: CAPABILITY_LABELS
    };
  },
  methods: {
    /** What the human may delegate HERE, which the server recomputes and clamps against on approve. */
    isOfferable(workspace, capability) {
      return (workspace.offerableCapabilities || []).includes(capability);
    },

    hasAccess(workspace) {
      return (this.value[workspace.id] || []).length > 0;
    },

    isGranted(workspace, capability) {
      return (this.value[workspace.id] || []).includes(capability);
    },

    /** An agent that reaches a workspace can always read it, so the row's tick is what removes it. */
    isLocked(workspace, capability) {
      return capability === 'read' && this.hasAccess(workspace) && this.isOfferable(workspace, 'read');
    },

    capabilityState(workspace, capability) {
      if (!this.isOfferable(workspace, capability)) return 'unavailable';
      return this.isGranted(workspace, capability) ? 'granted' : 'offerable';
    },

    emitGranted(workspaceId, capabilities) {
      const next = { ...this.value };
      if (capabilities.length) {
        next[workspaceId] = capabilities;
      } else {
        delete next[workspaceId];
      }
      this.$emit('input', next);
    },

    toggleCapability(workspace, capability) {
      if (!this.isOfferable(workspace, capability) || this.isLocked(workspace, capability)) return;
      const granted = this.value[workspace.id] || [];
      // Kept in `allCapabilities` order, and anything switched on brings `read` with it.
      const next = this.isGranted(workspace, capability)
        ? granted.filter((each) => each !== capability)
        : this.allCapabilities.filter(
            (each) =>
              each === capability || granted.includes(each) || (each === 'read' && this.isOfferable(workspace, 'read'))
          );
      this.emitGranted(workspace.id, next);
    },

    /** Opens at the least an agent can do, so adding a workspace never grants more than asked. */
    addWorkspace(workspace) {
      const offerable = workspace.offerableCapabilities || [];
      const first = offerable.includes('read') ? 'read' : offerable[0];
      if (first) this.emitGranted(workspace.id, [first]);
    },

    dropWorkspace(workspace) {
      this.emitGranted(workspace.id, []);
    }
  }
};
</script>

<style lang="scss" scoped>
// Anima's dark auth surface; `_auth-agentgrid.scss` remaps the colours for the light Agent Grid
// brand, as it does for the rest of this screen. The sizes are shared.
.workspace-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
  padding: 8px;
  list-style: none;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 14px;
  // The rows scroll rather than the page: the form is already the scrolling element.
  max-height: 260px;
  overflow-y: auto;
}

.workspace-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-height: 64px;
  padding: 8px 12px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.05);
}

.workspace-name {
  color: var(--white);
  font-size: 15px;
  // A long name gives way to the controls, which are what the row is for.
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.capability-chips,
.workspace-none {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.capability-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 40px;
  padding: 0 14px;
  border: 1px solid transparent;
  border-radius: 8px;
  background: none;
  color: var(--white);
  font-family: inherit;
  font-size: 15px;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;

  &.granted {
    background: var(--white);
    color: var(--dark-card-background, #1a1a1a);
  }

  // Filled, so it reads as on, but quieter than a chip that can still be switched off.
  &.locked {
    background: var(--transparent-white-50, rgba(255, 255, 255, 0.5));
    color: var(--dark-card-background, #1a1a1a);
    cursor: default;
  }

  &.offerable {
    border-color: rgba(255, 255, 255, 0.25);

    &:hover {
      border-color: rgba(255, 255, 255, 0.5);
    }
  }

  // Nothing to press: the word alone.
  &.unavailable {
    color: var(--transparent-white-30, rgba(255, 255, 255, 0.3));
    cursor: default;
  }
}

.workspace-drop,
.workspace-add {
  display: flex;
  align-items: center;
  gap: 6px;
  height: 40px;
  padding: 0 14px;
  border-radius: 8px;
  font-family: inherit;
  font-size: 15px;
  cursor: pointer;
}

.workspace-drop {
  justify-content: center;
  width: 40px;
  padding: 0;
  border: 1px solid rgba(255, 255, 255, 0.25);
  background: none;
  color: var(--white);

  &:hover {
    border-color: rgba(255, 255, 255, 0.5);
  }
}

.workspace-add {
  border: 1px solid transparent;
  background: var(--white);
  color: var(--dark-card-background, #1a1a1a);
}

.workspace-add-plus {
  font-size: 19px;
  line-height: 1;
}

.no-access {
  color: var(--transparent-white-50);
  font-size: 13px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
</style>
